import type { ScanResult } from './scanResult';

export type ScanRefusal = { title: string; message: string };

/**
 * Why a screen that needs a serialised item cannot use this scan, or null when it can.
 *
 * Every one of these screens used to `return` on a missing serial number, which on a handheld is
 * indistinguishable from the scan never arriving — the operator pulls the trigger, the scanner
 * beeps, and the screen does not move. The commonest cause is not a fault at all: a pharmaceutical
 * pack carries a linear barcode as well as a 2D DataMatrix, only the DataMatrix holds the serial
 * number, and a handheld aimed at the wrong one reads the wrong barcode perfectly.
 */
export const explainUnusableSerialScan = (result: ScanResult): ScanRefusal | null => {
  if (result.serialNumber) return null;

  if (result.gtin) {
    return {
      title: 'No serial number in that barcode',
      message:
        'That is the striped barcode on the pack, which only identifies the product. Scan the ' +
        'small square 2D code instead — the serial number is only in that one.'
    };
  }

  if (result.kind === 'raw') {
    return {
      title: 'Not a product barcode',
      message: `“${result.text}” is not a GS1 product barcode. Check you are scanning the code on the pack.`
    };
  }

  return {
    title: 'No serial number in that barcode',
    message: 'That barcode carries no serial number. Scan the 2D code on the pack instead.'
  };
};
